"use client";

import { useEffect, useState } from "react";
import { ArrowRight, Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetClose } from "@/components/ui/sheet";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
function DesktopNavConsumer({
  scrolled
}: {
  scrolled: boolean;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex gap-2">
        <div className="h-8 w-20 bg-muted/40 animate-pulse rounded-lg" />
        <div className="h-8 w-20 bg-muted/40 animate-pulse rounded-lg" />
        <div className="h-8 w-20 bg-muted/40 animate-pulse rounded-lg" />
        <div className="h-8 w-20 bg-muted/40 animate-pulse rounded-lg" />
      </div>;
  }
  if (scrolled) {
    return <nav className="flex items-center gap-10">
        {menuItems.map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} className="text-foreground hover:text-accent font-default text-base uppercase tracking-wide transition-colors">
            {item.label}
          </Link>)}
      </nav>;
  }
  return <nav className="flex items-center gap-1 rounded-lg border border-slate-glass bg-white/5 px-2 py-2 backdrop-blur-md">
      {menuItems.map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} className="text-foreground hover:bg-white/10 font-default text-base uppercase tracking-wide px-4 py-2 rounded-lg transition-colors">
          {item.label}
        </Link>)}
    </nav>;
}
function MobileNavConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-6 px-6">
        <div className="h-12 w-3/4 bg-muted/40 animate-pulse rounded-lg" />
        <div className="h-12 w-2/3 bg-muted/40 animate-pulse rounded-lg" />
        <div className="h-12 w-1/2 bg-muted/40 animate-pulse rounded-lg" />
      </div>;
  }
  return <nav className="flex flex-col gap-2 px-6 mt-8">
      {menuItems.map((item, i) => <SheetClose key={item.id} asChild>
          <Link data-index={i} to={item.href || "#"} className="font-default text-2xl text-foreground hover:text-accent transition-colors py-3 border-b border-slate-glass">
            {item.label}
          </Link>
        </SheetClose>)}
    </nav>;
}
export default function CabeceraPrincipal() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 80);
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section data-section-id="719" id="cabecera-principal" className={`bg-background text-foreground sticky top-0 z-50 w-full transition-all duration-300 ${scrolled ? "bg-background/90 backdrop-blur-lg border-b border-slate-glass" : "bg-transparent"}`}>
      <div className="container mx-auto px-4 md:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20 md:h-24">
          {}
          <div className="flex-shrink-0">
            <Link to="/" aria-label="TimeArrival home">
              <WvcLogo className="h-8 w-auto" />
            </Link>
          </div>

          {}
          <div className="hidden lg:flex flex-1 justify-center">
            <MenuProvider menu_id="16">
              <DesktopNavConsumer scrolled={scrolled} />
            </MenuProvider>
          </div>

          {}
          <div className="hidden lg:flex flex-shrink-0">
            <Button asChild size="lg" className="rounded-lg bg-accent text-accent-foreground hover:bg-accent/90 font-default uppercase tracking-widest text-sm shadow-lg transition-all hover:translate-y-[-2px]">
              <Link to="/BookDemoPage">SOLICITAR PRESENTACION<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="lucide lucide-arrow-right ml-1 h-4 w-4" aria-hidden="true" data-ast-id="f:/src/reusable_sections/Header.tsx|sig:jnkw0x" data-ast-start="5818" data-ast-end="5857"><path d="M5 12h14"></path><path d="m12 5 7 7-7 7"></path></svg></Link>
            </Button>
          </div>

          {}
          <div className="lg:hidden flex items-center gap-3">
            <Button asChild size="sm" className="rounded-lg bg-accent text-accent-foreground hover:bg-accent/90 font-default uppercase tracking-wider text-xs shadow-md">
              <Link to="/BookDemoPage">Request Demo</Link>
            </Button>
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" aria-label="Abrir menú" className="text-foreground hover:bg-white/10 rounded-lg">
                  <Menu className="h-6 w-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="bg-background/95 backdrop-blur-xl text-foreground border-l border-slate-glass w-full sm:max-w-md p-0">
                <SheetHeader className="px-6 pt-6 pb-2 flex flex-row items-center justify-between space-y-0">
                  <SheetTitle className="font-default">
                    <WvcLogo className="h-7 w-auto" />
                  </SheetTitle>
                  <SheetClose asChild>
                    <Button variant="ghost" size="icon" aria-label="Cerrar menú" className="text-foreground hover:bg-white/10 rounded-lg">
                      <X className="h-6 w-6" />
                    </Button>
                  </SheetClose>
                </SheetHeader>

                <MenuProvider menu_id="16">
                  <MobileNavConsumer />
                </MenuProvider>

                <div className="px-6 mt-10">
                  <SheetClose asChild>
                    <Button asChild size="lg" className="w-full rounded-lg bg-accent text-accent-foreground hover:bg-accent/90 font-default uppercase tracking-widest text-sm shadow-lg">
                      <Link to="/BookDemoPage">
                        Request Demo
                        <ArrowRight className="ml-1 h-4 w-4" />
                      </Link>
                    </Button>
                  </SheetClose>
                </div>

                <div className="px-6 mt-8 font-default text-xs uppercase tracking-widest text-muted-foreground">
                  TimeArrival © 2024
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </section>;
}